import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, StarFour, Plus, X } from "@phosphor-icons/react";
import { api } from "@/api";

export function WatchlistPanel({ pairs = [], onSelect }) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");

  const wl = useQuery({
    queryKey: ["watchlist"],
    queryFn: api.getWatchlist,
    refetchInterval: 30_000,
  });

  const save = useMutation({
    mutationFn: (symbols) => api.setWatchlist(symbols),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["watchlist"] });
      qc.invalidateQueries({ queryKey: ["status"] });
      toast.success(`Watchlist saved · ${d.symbols.length} symbols`);
    },
    onError: () => toast.error("Failed to save watchlist"),
  });

  const symbols = wl.data?.symbols || [];
  const suggested = pairs.slice(0, 24).map((p) => p.symbol).filter((s) => !symbols.includes(s));

  const add = (sym) => {
    const s = (sym || "").trim().toUpperCase();
    if (!s) return;
    if (symbols.includes(s)) {
      toast.info(`${s} already pinned`);
      return;
    }
    if (symbols.length >= 10) {
      toast.error("Max 10 pins. Unpin one first.");
      return;
    }
    save.mutate([...symbols, s]);
    setInput("");
  };

  const remove = (sym) => save.mutate(symbols.filter((s) => s !== sym));

  return (
    <div data-testid="watchlist-panel" className="border border-white/10 bg-[#0A0A0A]">
      <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-2">
        <StarFour size={16} weight="fill" className="text-[#F5C518]" />
        <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Symbol Focus</h2>
        <span className="ml-auto font-mono text-[11px] text-white/40">
          {symbols.length}/10 pinned
        </span>
      </div>

      <div className="p-4 space-y-3">
        <p className="text-white/50 font-mono text-[11px] leading-relaxed">
          Pin up to 10 favourite pairs. The scanner will prioritise these across all modes so signals fire faster on the tickers you actually trade.
        </p>

        <div className="flex flex-wrap gap-2" data-testid="watchlist-chips">
          {symbols.length === 0 && (
            <span className="text-white/40 font-mono text-[11px]">None yet — add your first pair below.</span>
          )}
          {symbols.map((s) => (
            <button
              key={s}
              data-testid={`wl-chip-${s}`}
              onClick={() => onSelect?.(s)}
              className="group inline-flex items-center gap-1 font-mono text-[11px] px-2 py-1 border border-[#F5C518]/40 bg-[#F5C518]/10 text-[#F5C518] hover:bg-[#F5C518]/20"
            >
              <Star size={11} weight="fill" />
              {s}
              <span
                role="button"
                tabIndex={0}
                data-testid={`wl-remove-${s}`}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s);
                }}
                className="ml-1 opacity-70 group-hover:opacity-100 hover:text-white cursor-pointer"
              >
                <X size={10} />
              </span>
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            add(input);
          }}
          className="flex items-center gap-2"
        >
          <input
            data-testid="wl-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="e.g. BTCUSDT"
            list="pair-suggestions"
            className="flex-1 bg-black border border-white/15 rounded-none px-3 py-1.5 text-white font-mono text-[12px] focus:outline-none focus:border-[#F5C518]/60"
          />
          <datalist id="pair-suggestions">
            {pairs.map((p) => (
              <option key={p.symbol} value={p.symbol} />
            ))}
          </datalist>
          <button
            data-testid="wl-add-btn"
            type="submit"
            disabled={save.isPending}
            className="font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border border-[#F5C518]/50 bg-[#F5C518]/10 text-[#F5C518] hover:bg-[#F5C518]/20 flex items-center gap-1"
          >
            <Plus size={11} /> Pin
          </button>
        </form>

        {suggested.length > 0 && (
          <div>
            <div className="text-white/40 font-mono text-[10px] uppercase tracking-widest mb-1">
              Top movers · one-tap pin
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggested.slice(0, 16).map((s) => (
                <button
                  key={s}
                  data-testid={`wl-suggest-${s}`}
                  onClick={() => add(s)}
                  className="font-mono text-[10px] px-2 py-0.5 border border-white/15 text-white/70 hover:border-[#F5C518]/50 hover:text-[#F5C518]"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
